import { Box } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonDateInput } from 'components/common/form/date';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import {
  CommonTextInput,
  DEFAULT_TEXT_DEBOUNCE_MS,
} from 'components/common/form/text';
import { CommonTextareaInput } from 'components/common/form/textarea';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { TeamsContext } from 'contexts/admin/teams.context';
import { parseISO } from 'date-fns';
import { t } from 'i18next';
import { MlbHelper } from 'lib_ts/classes/mlb.helper';
import { TeamsHelper } from 'lib_ts/classes/teams.helper';
import { MlbAbbreviation } from 'lib_ts/enums/batrack.enums';
import { HardwareVersion } from 'lib_ts/enums/machine.enums';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { COMPETITION_OPTIONS, Competition } from 'lib_ts/enums/team.enums';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { first, isNil } from 'lodash';
import { useContext, useMemo, useRef, useState } from 'react';

const NUM_COLS = 6;
const FULL_COL = 'span 6';
const HALF_COL = 'span 3';
const THIRD_COL = 'span 2';

interface IProps {
  machine: Partial<IMachine>;
  updateMetadata: (model: Partial<IMachine>) => void;
}

export const MachineDetailsTab = ({ machine, updateMetadata }: IProps) => {
  const currentDateRef = useRef(new Date());
  const { teams } = useContext(TeamsContext);

  // setup competition based on selected team
  const [competition, setCompetition] = useState(
    teams.find((m) => m._id === machine._parent_id)?.league
  );

  // narrow team options based on competition
  const teamOptions = useMemo(() => {
    return teams
      .filter((m) => !competition || m.league === competition)
      .map((m) => ({
        label: m.name ?? m._id,
        value: m._id,
      }));
  }, [competition, teams]);

  const team = useMemo(
    () => teams.find((m) => m._id === machine._parent_id),
    [teams, machine._parent_id]
  );

  const affiliateOptions = useMemo(
    () => TeamsHelper.getAffiliateOptions(competition, team?.stats_api_orgId),
    [competition, team?.stats_api_orgId]
  );

  const updating = useMachinesPortalStore((s) => s.updating);

  const suggestedNickname = useMemo(() => {
    if (isNil(team?.stats_api_orgId)) {
      // don't suggest any nicknames for non-MLB teams
      return undefined;
    }

    // like TOR or NYY
    const orgCode =
      team?.abbreviation && team.abbreviation !== MlbAbbreviation.None
        ? team.abbreviation
        : undefined;

    const affiliatePk = (machine.affiliateTeamPk ?? -1).toString();
    const affiliateOption = affiliateOptions.find(
      (o) => o.value === affiliatePk
    );

    const teamLevel = affiliateOption?.sportId
      ? MlbHelper.getLevelFromSportId(affiliateOption.sportId)
      : undefined;

    const teamName = affiliateOption?.label;

    return [orgCode, teamLevel, teamName].filter((m) => m).join('-');
  }, [
    team?.stats_api_orgId,
    team?.abbreviation,
    machine.affiliateTeamPk,
    affiliateOptions,
  ]);

  return (
    <CommonFormGrid columns={NUM_COLS}>
      <Box gridColumn={HALF_COL}>
        <CommonSelectInput
          id="team-competition"
          name="competition"
          label="Competition"
          options={COMPETITION_OPTIONS}
          value={competition}
          onChange={(v) => {
            // reset the parent since the current value will not be in the new competition level
            updateMetadata({
              _parent_id: '',
            });

            setCompetition(v as Competition);
          }}
          optional
          skipSort
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonSearchInput
          id="machine-editor-_parent_id"
          name="_parent_id"
          label="admin.parent-organization"
          options={teamOptions}
          values={machine._parent_id ? [machine._parent_id] : []}
          onChange={(v) => {
            if (
              machine.realTimeApiUrl &&
              machine.realTimeApiUrl.startsWith('http')
            ) {
              NotifyHelper.warning({
                message_md:
                  'Automatically disabling Real-Time API connection because organization changed. Please clear or update the URL before continuing.',
              });

              updateMetadata({
                realTimeApiUrl: '*' + machine.realTimeApiUrl,
              });
            }

            updateMetadata({
              _parent_id: first(v),
              _parent_def: PitchListOwner.Team,
              _parent_field: 'machines',
            });
          }}
          disabled={updating}
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonSelectInput
          id="machine-editor-affiliateTeamPk"
          name="affiliateTeamPk"
          label="Affiliate Team"
          options={affiliateOptions}
          value={machine.affiliateTeamPk?.toString()}
          onOptionalNumericChange={(v) =>
            updateMetadata({
              affiliateTeamPk: v,
            })
          }
          disabled={updating}
          optional
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonTextInput
          id="machine-editor-nickname"
          label="Nickname"
          value={machine.nickname}
          debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
          onChange={(v) => updateMetadata({ nickname: v })}
          disabled={updating}
          hint_md={
            suggestedNickname ? `Suggestion: ${suggestedNickname}` : undefined
          }
          optional
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonSelectInput
          id="machine-editor-ignore-game-status"
          name="ignore_game_status"
          label="Ignore Game Status"
          options={[
            { label: 'No', value: 'false' },
            { label: 'Yes', value: 'true' },
          ]}
          value={machine.ignore_game_status?.toString()}
          onOptionalBooleanChange={(v) =>
            updateMetadata({ ignore_game_status: v })
          }
          hint_md="Allow machine to be used regardless of whether its parent team is in a game."
          disabled={updating}
          optional
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonSelectInput
          id="machine-editor-sandbox"
          name="sandbox"
          label="Sandbox"
          options={[
            { label: 'No', value: 'false' },
            { label: 'Yes', value: 'true' },
          ]}
          value={machine.sandbox?.toString()}
          onOptionalBooleanChange={(v) => {
            updateMetadata({
              sandbox: v,
              suppress_data: v || machine.suppress_data,
              rapsodo_serial: v ? machine.machineID : machine.rapsodo_serial,
            });
          }}
          disabled={updating}
          optional
        />
      </Box>
      <Box gridColumn={THIRD_COL}>
        <CommonSelectInput
          id="machine-editor-hardware-version"
          name="hardware_version"
          label="Hardware Version"
          options={Object.values(HardwareVersion).map((t) => ({
            label: t,
            value: t,
            group: t.includes('Refurbished') ? 'Refurbished' : 'New',
          }))}
          value={machine.hardware_version}
          onChange={(v) =>
            updateMetadata({ hardware_version: v as HardwareVersion })
          }
          disabled={updating}
        />
      </Box>
      <Box gridColumn={THIRD_COL}>
        <CommonDateInput
          id="machine-editor-last-hardware-change"
          label="Last Hardware Change"
          iconTooltip="Create a machine service log from to update this value."
          defaultValue={
            machine.last_hardware_changed
              ? parseISO(machine.last_hardware_changed)
              : undefined
          }
          maxDate={currentDateRef.current}
          onChange={(date) => {
            if (!date) {
              NotifyHelper.warning({
                message_md: t('common.check-inputs-msg'),
              });
              return;
            }
            updateMetadata({
              last_hardware_changed: date.toISOString(),
            });
          }}
          hint_md="Shots fired prior to this date will not be usable for training, aiming or modelling purposes."
          disabled
          showTime
        />
      </Box>
      <Box gridColumn={THIRD_COL}>
        <CommonTextInput
          id="machine-editor-airtable-record-id-hw-config"
          name="airtable-record-id-hw-config"
          label="Airtable Record ID (HW Config)"
          placeholder="(optional)"
          value={machine.at_record_id_hw_config ?? undefined}
          debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
          onChange={(v) =>
            updateMetadata({ at_record_id_hw_config: v?.trim() })
          }
          disabled={updating}
          hint_md="e.g. recXXXXXXXXXXXXXX"
          optional
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonSelectInput
          id="machine-editor-enable-auto-update-model"
          name="enable_auto_update_model"
          label="Auto-Update Model"
          options={[
            { label: 'common.yes', value: 'true' },
            { label: 'common.no', value: 'false' },
          ]}
          value={machine.enable_auto_update_model?.toString()}
          onBooleanChange={(v) =>
            updateMetadata({ enable_auto_update_model: v })
          }
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonSelectInput
          id="machine-editor-enable-stereo-vision"
          name="enable_trajekt_vision"
          label="Enable Trajekt Vision"
          options={[
            { label: 'common.yes', value: 'true' },
            { label: 'common.no', value: 'false' },
          ]}
          value={machine.enable_trajekt_vision?.toString()}
          onBooleanChange={(v) => updateMetadata({ enable_trajekt_vision: v })}
          disabled={updating}
        />
      </Box>
      <Box gridColumn={THIRD_COL}>
        <CommonSelectInput
          id="machine-editor-display-active-hitter"
          name="display_active_hitter"
          label="Display Active Hitter"
          disabled={updating}
          options={[
            { label: 'No', value: 'false' },
            { label: 'Yes', value: 'true' },
          ]}
          value={machine.enable_recap_hitter_name?.toString()}
          onOptionalBooleanChange={(v) =>
            updateMetadata({
              enable_recap_hitter_name: v,
            })
          }
          optional
        />
      </Box>
      <Box gridColumn={THIRD_COL}>
        <CommonSelectInput
          id="machine-editor-data-aggregation"
          name="suppress_data"
          label="Data Aggregation"
          disabled={machine.sandbox || updating}
          options={[
            { label: 'Enabled', value: 'false' },
            { label: 'Disabled', value: 'true' },
          ]}
          value={machine.suppress_data?.toString()}
          onOptionalBooleanChange={(v) =>
            updateMetadata({
              suppress_data: v,
            })
          }
          hint_md={
            machine.sandbox
              ? 'Sandbox data is suppressed for data aggregation.'
              : "Suppresses the machine's data in aggregation (e.g. for dashboards)."
          }
          optional
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonSelectInput
          id="machine-editor-enable-tracking-debug"
          name="enable_tracking_debug"
          label="Enable Tracking Debug"
          options={[
            { label: 'common.yes', value: 'true' },
            { label: 'common.no', value: 'false' },
          ]}
          value={machine.enable_tracking_debug?.toString()}
          onBooleanChange={(v) => updateMetadata({ enable_tracking_debug: v })}
          disabled={updating}
          hint_md={
            machine.enable_tracking_debug
              ? 'Data from all tracking devices will be available for Session Export, Customer API, and Real-Time API'
              : 'Only data from the active tracking devices will be available for Session Export, Customer API, and Real-Time API'
          }
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonSelectInput
          id="machine-editor-access-level"
          name="super"
          label="Access Level"
          options={[
            { label: 'Basic', value: 'false' },
            { label: 'Super', value: 'true' },
          ]}
          value={machine.super?.toString()}
          onOptionalBooleanChange={(v) => updateMetadata({ super: v })}
          hint_md="Super machines have access to all videos in the database."
          disabled={updating}
          optional
        />
      </Box>
      <Box gridColumn={FULL_COL}>
        <CommonTextInput
          id="machine-editor-real-time-api-url"
          name="real-time-api-url"
          label="Real-Time API URL(s)"
          placeholder="(optional)"
          value={machine.realTimeApiUrl ?? undefined}
          debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
          onChange={(v) =>
            updateMetadata({ realTimeApiUrl: v?.trim() ?? null })
          }
          disabled={updating}
          hint_md="e.g. https://url.one,https://url.two"
          optional
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonTextInput
          id="machine-editor-real-time-api-header-key"
          name="real-time-api-header-key"
          label="Real-Time API Header Key"
          placeholder="(optional)"
          value={machine.realTimeApiHeaderKey ?? undefined}
          debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
          onChange={(v) =>
            updateMetadata({ realTimeApiHeaderKey: v?.trim() ?? null })
          }
          disabled={updating}
          optional
        />
      </Box>
      <Box gridColumn={HALF_COL}>
        <CommonTextInput
          id="machine-editor-real-time-api-header-value"
          name="real-time-api-header-value"
          label="Real-Time API Header Value"
          placeholder="(optional)"
          value={machine.realTimeApiHeaderValue ?? undefined}
          debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
          onChange={(v) =>
            updateMetadata({ realTimeApiHeaderValue: v?.trim() ?? null })
          }
          disabled={updating}
          optional
        />
      </Box>
      <Box gridColumn={FULL_COL}>
        <CommonTextareaInput
          id="machine-editor-internal-notes"
          label="Internal Notes"
          placeholder="e.g. team name, location, contact person"
          value={machine.notes}
          debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
          onChange={(v) => updateMetadata({ notes: v })}
          rows={4}
          disabled={updating}
        />
      </Box>
    </CommonFormGrid>
  );
};
